import React, { useState, useEffect } from "react";
import MonacoEditor from "react-monaco-editor";
import { Eraser, Copy, WrapText, AArrowDown, AArrowUp, Wand2 } from "lucide-react";
import { toast } from "react-toastify";
import { formatCode, isFormattable } from "../lib/formatCode";
import "./Styles.css";

const CodeEditor = ({ label, code, onChange, onClear, onCopy }) => {
  const [wordWrap, setWordWrap] = useState(false);
  const [fontSize, setFontSize] = useState(14);
  const [formatting, setFormatting] = useState(false);
  const language = label.toLowerCase();
  const formattable = isFormattable(language);

  const toggleWordWrap = () => {
    setWordWrap(!wordWrap);
  };

  const increaseFontSize = () => {
    setFontSize(fontSize + 1);
  };

  const decreaseFontSize = () => {
    setFontSize(fontSize - 1);
  };

  const handleFormat = async () => {
    if (!formattable || formatting) return;
    setFormatting(true);
    try {
      const formatted = await formatCode(code, language);
      onChange(formatted);
    } catch (err) {
      toast.error(`Could not format ${label} — check for syntax errors.`);
    } finally {
      setFormatting(false);
    }
  };

  return (
    <div className="mb-2 flex h-full w-full flex-col overflow-hidden rounded-sm border border-vscode-border bg-vscode-panel">
      <div className="flex w-full items-center justify-between border-b border-vscode-border px-3 py-1.5">
        <label className="text-xs font-semibold uppercase tracking-wide text-vscode-textMuted">
          {label}
        </label>

        <div className="flex gap-1 text-vscode-textMuted" id="options">
          <button
            onClick={handleFormat}
            disabled={!formattable || formatting}
            title="Format Document"
            className="rounded-sm p-1 hover:bg-vscode-listHover hover:text-vscode-textBright disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
          >
            <Wand2 size={14} strokeWidth={1.5} />
          </button>
          <button
            onClick={increaseFontSize}
            title="Increase Font Size"
            className="rounded-sm p-1 hover:bg-vscode-listHover hover:text-vscode-textBright"
          >
            <AArrowUp size={16} strokeWidth={1.5} />
          </button>
          <button
            onClick={decreaseFontSize}
            title="Decrease Font Size"
            className="rounded-sm p-1 hover:bg-vscode-listHover hover:text-vscode-textBright"
          >
            <AArrowDown size={16} strokeWidth={1.5} />
          </button>
          <button
            onClick={() => onCopy(code)}
            title="Copy"
            className="rounded-sm p-1 hover:bg-vscode-listHover hover:text-vscode-textBright"
          >
            <Copy size={14} strokeWidth={1.5} />
          </button>
          <button
            onClick={toggleWordWrap}
            title="Toggle Word Wrap"
            className="rounded-sm p-1 hover:bg-vscode-listHover hover:text-vscode-textBright"
          >
            <WrapText size={14} strokeWidth={1.5} />
          </button>
          <button
            onClick={onClear}
            title="Clear"
            className="rounded-sm p-1 hover:bg-vscode-listHover hover:text-vscode-red"
          >
            <Eraser size={14} strokeWidth={1.5} />
          </button>
        </div>
      </div>
      <MonacoEditor
        height="25vh"
        width={`100%`}
        language={label.toLowerCase()}
        value={code}
        onChange={onChange}
        theme="vs-dark"
        options={{
          minimap: { enabled: false },
          fontSize: fontSize,
          fontFamily: "'Fira Code', Consolas, 'Courier New', monospace",
          fontLigatures: true,
          wordWrap: wordWrap ? "on" : "off",
        }}
      />
    </div>
  );
};

const WebDevEditor = () => {
  const htmlcodeKey = "htmlcode";
  const csscodeKey = "csscode";
  const jscodeKey = "jscode";

  const [htmlCode, setHtmlCode] = useState(
    localStorage.getItem(htmlcodeKey) || ""
  );
  const [cssCode, setCssCode] = useState(
    localStorage.getItem(csscodeKey) || ""
  );
  const [jsCode, setJsCode] = useState(localStorage.getItem(jscodeKey) || "");
  const [previewDoc, setPreviewDoc] = useState("");

  useEffect(() => {
    localStorage.setItem(htmlcodeKey, htmlCode);
  }, [htmlCode, htmlcodeKey]);

  useEffect(() => {
    localStorage.setItem(csscodeKey, cssCode);
  }, [cssCode, csscodeKey]);

  useEffect(() => {
    localStorage.setItem(jscodeKey, jsCode);
  }, [jsCode, jscodeKey]);

  useEffect(() => {
    // Debounced + rebuilt as a whole document each time so edits don't stack
    // scripts, intervals, or listeners from previous runs on top of each other.
    const timeout = setTimeout(() => {
      setPreviewDoc(
        `<!DOCTYPE html><html><head><style>${cssCode}</style></head><body>${htmlCode}<script>${jsCode}<` +
          `/script></body></html>`
      );
    }, 300);
    return () => clearTimeout(timeout);
  }, [htmlCode, cssCode, jsCode]);

  const handleHtmlClear = () => {
    setHtmlCode("");
  };

  const handleCSSClear = () => {
    setCssCode("");
  };

  const handleJSClear = () => {
    setJsCode("");
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="flex h-full w-full flex-col gap-2 bg-vscode-bg p-4">
      <div className="flex w-full gap-2 overflow-x-auto" id="editors">
        <CodeEditor
          width="100%"
          label="HTML"
          code={htmlCode}
          onChange={setHtmlCode}
          onClear={handleHtmlClear}
          onCopy={handleCopy}
        />
        <CodeEditor
          label="CSS"
          width="100%"
          code={cssCode}
          onChange={setCssCode}
          onClear={handleCSSClear}
          onCopy={handleCopy}
        />
        <CodeEditor
          width="100%"
          label="JavaScript"
          code={jsCode}
          onChange={setJsCode}
          onClear={handleJSClear}
          onCopy={handleCopy}
        />
      </div>
      <iframe
        id="output"
        title="Output"
        srcDoc={previewDoc}
        sandbox="allow-scripts"
        className="h-[87vh] rounded-sm border border-vscode-border bg-white"
      />
    </div>
  );
};

export default WebDevEditor;
